/**
 * Frame the ring to a marketplace's rules, host-side, with no viewer change.
 *
 * The viewer renders what the camera sees; a marketplace wants the product at a
 * fixed share of the frame (Amazon: ~85%) on a fixed background (Amazon: pure
 * RGB 255 white). So:
 *
 *   1. render once with a TRANSPARENT background — its alpha channel is the
 *      ring's silhouette, which gives an exact bounding box;
 *   2. for a "keep the scene" preset, render again normally, same camera and
 *      size; for a "white" preset, reuse the transparent render;
 *   3. crop a window of the preset's aspect around the ring so the ring spans
 *      `fill` of it, and draw that into the output canvas — onto pure white
 *      for "white", onto the scene's own background colour otherwise.
 *
 * Compositing onto white in the HOST is what makes "pure white" exact: a
 * scene's white background goes through tone mapping and bloom inside the
 * viewer and may come out 254.
 *
 * The geometry (`alphaBox`, `cropWindow`) is pure so it can be checked without
 * a browser.
 */

export interface Box { x: number; y: number; w: number; h: number }

/**
 * Tight box around pixels whose alpha exceeds `threshold` (0..255). A soft
 * ground shadow is faint (low alpha), so the default threshold measures the
 * ring itself, not its shadow. Returns null for an empty frame.
 */
export function alphaBox(data: Uint8ClampedArray, width: number, height: number, threshold = 128): Box | null {
  let x0 = width, y0 = height, x1 = -1, y1 = -1;
  for (let y = 0; y < height; y++) {
    const row = y * width * 4;
    for (let x = 0; x < width; x++) {
      if (data[row + x * 4 + 3] > threshold) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return null;
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/**
 * The source-space window, of the OUTPUT's aspect, centred on the ring, in
 * which the ring spans exactly `fill` of whichever side binds.
 */
export function cropWindow(ring: Box, outW: number, outH: number, fill: number): Box {
  const aspect = outW / outH;
  // Window height such that the ring fits `fill` both ways.
  const hByHeight = ring.h / fill;
  const hByWidth = ring.w / fill / aspect;
  const h = Math.max(hByHeight, hByWidth);
  const w = h * aspect;
  const cx = ring.x + ring.w / 2;
  const cy = ring.y + ring.h / 2;
  return { x: cx - w / 2, y: cy - h / 2, w, h };
}

// ─────────────────────────────────────────────── browser half

interface SnapshotApi {
  snapshot(opts: {
    width?: number;
    height?: number;
    transparent?: boolean;
    format?: "png" | "jpeg" | "webp";
    quality?: number;
    frames?: number;
  }): Promise<Blob>;
}

export interface FramedOptions {
  width: number;
  height: number;
  fill: number;
  /** "white": pure 255 white. "keep": the scene's own background. "transparent": PNG alpha. */
  background: "white" | "keep" | "transparent";
  format: "jpeg" | "png";
  /** Converge frames before reading pixels (progressive accumulation). */
  frames?: number;
}

export interface FramedResult {
  blob: Blob;
  /** Measured: the ring's span on the binding side, as a fraction of the output. */
  ringFill: number;
}

/** Largest side we ask the viewer for; beyond this it would need a tiled render. */
const MAX_RENDER = 4096;

async function toBitmap(blob: Blob): Promise<ImageBitmap> {
  return createImageBitmap(blob);
}

function readPixels(bmp: ImageBitmap): ImageData {
  const c = document.createElement("canvas");
  c.width = bmp.width;
  c.height = bmp.height;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  if (!ctx) throw new Error("Could not read the render.");
  ctx.drawImage(bmp, 0, 0);
  return ctx.getImageData(0, 0, bmp.width, bmp.height);
}

/** The scene's background colour: the most common corner pixel of a normal render. */
function cornerColour(img: ImageData): string {
  const { data, width, height } = img;
  const at = (x: number, y: number) => {
    const i = (y * width + x) * 4;
    return `rgb(${data[i]},${data[i + 1]},${data[i + 2]})`;
  };
  const corners = [at(2, 2), at(width - 3, 2), at(2, height - 3), at(width - 3, height - 3)];
  const counts = new Map<string, number>();
  for (const c of corners) counts.set(c, (counts.get(c) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0][0];
}

export async function renderFramed(r: SnapshotApi, o: FramedOptions): Promise<FramedResult> {
  // Render bigger than the output so the crop keeps full resolution: the ring
  // usually fills ~60% of the camera's view, and the crop scales it to `fill`.
  const aspect = o.width / o.height;
  const scale = Math.min(MAX_RENDER / Math.max(o.width, o.height), 1.4);
  const rw = Math.round(o.width * scale);
  const rh = Math.round(rw / aspect);
  const frames = o.frames ?? 24;

  const maskBlob = await r.snapshot({ width: rw, height: rh, transparent: true, format: "png", frames });
  const maskBmp = await toBitmap(maskBlob);
  const mask = readPixels(maskBmp);
  const ring = alphaBox(mask.data, mask.width, mask.height);
  if (!ring) throw new Error("There is nothing in view to photograph.");
  const win = cropWindow(ring, o.width, o.height, o.fill);

  let source: ImageBitmap = maskBmp;
  let ground = "#ffffff";
  if (o.background === "keep") {
    const sceneBlob = await r.snapshot({ width: rw, height: rh, transparent: false, format: "png", frames });
    source = await toBitmap(sceneBlob);
    ground = cornerColour(readPixels(source));
  }

  const out = document.createElement("canvas");
  out.width = o.width;
  out.height = o.height;
  const ctx = out.getContext("2d");
  if (!ctx) throw new Error("Could not prepare the image.");
  if (o.background !== "transparent") {
    ctx.fillStyle = ground;
    ctx.fillRect(0, 0, o.width, o.height);
  }
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(source, win.x, win.y, win.w, win.h, 0, 0, o.width, o.height);
  maskBmp.close();
  if (source !== maskBmp) source.close();

  const type = o.format === "png" || o.background === "transparent" ? "image/png" : "image/jpeg";
  const blob = await new Promise<Blob>((res, rej) =>
    out.toBlob((b) => (b ? res(b) : rej(new Error("Could not encode the image."))), type, 0.93),
  );
  const ringFill = Math.max((ring.w / win.w), (ring.h / win.h));
  return { blob, ringFill };
}
